import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class DateTimeInterceptor implements HttpInterceptor {

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Convert NTZ dates in request body before sending
    if (req.body) {
      req = req.clone({ body: this.convertNTZDatesToUTC(req.body) });
    }

    return next.handle(req).pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          event = event.clone({ body: this.convertDates(event.body) });
        }
        return event;
      })
    );
  }

  private convertDates(body: any): any {
    if (body === null || body === undefined) return body;

    // handle arrays
    if (Array.isArray(body)) {
      return body.map(item => this.convertDates(item));
    }

    if (typeof body !== 'object') {
      return body;
    }

    for (const key of Object.keys(body)) {
      const value = body[key];
      if (this.isIsoDateString(value)) {
        if (!key.includes('NTZ')) {
          body[key] = this.convertToLocalDate(value);
        }
        // console.log("date response " + key + " = ", body[key]);
      } else if (typeof value === 'object' && value !== null) {
        body[key] = this.convertDates(value);
      }
    }
    return body;
  }

  private isIsoDateStringOrDateOnly(value: any): boolean {
    if (typeof value !== 'string') return false;
    // accept full ISO with time and timezone, or date-only YYYY-MM-DD
    return /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[\+\-]\d{2}:\d{2})?)?$/.test(value);
  }

  private isIsoDateString(value: any): boolean {
    return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[\+\-]\d{2}:\d{2})?$/.test(value);
  }

  private convertToLocalDate(value: string): string {
    // value is ISO from server (likely UTC or with offset).
    const utcDate = new Date(value);
    // create a Date that represents local time equivalent, then return ISO (with timezone Z)
    const localMillis = utcDate.getTime() - (utcDate.getTimezoneOffset() * 60000);
    return new Date(localMillis).toISOString();
  }

  private convertNTZDatesToUTC(body: any): any {
    if (body === null || body === undefined) return body;
    // arrays
    if (Array.isArray(body)) {
      return body.map(item => this.convertNTZDatesToUTC(item));
    }

    if (typeof body !== 'object') return body;

    for (const key of Object.keys(body)) {
      const value = body[key];
      if (this.isDateLike(value)) {
        if (key.includes('NTZ')) { body[key] = this.convertToUTCString(value); }
        // console.log("date request " + key + " = ", body[key]);
      } else if (typeof value === 'object' && value !== null) {
        body[key] = this.convertNTZDatesToUTC(value);
      }
    }
    return body;
  }

  private isDateLike(value: any): boolean {
    if (!value && value !== 0) return false;
    if (value instanceof Date) return true;
    if (typeof value === 'number' && isFinite(value)) return true; // timestamp
    if (typeof value === 'string' && this.isIsoDateStringOrDateOnly(value)) return true;
    return false;
  }

  private convertToUTCString(value: string | number | Date): string {
    let date: Date;

    if (value instanceof Date) {
      date = value;
    } else if (typeof value === 'number') {
      date = new Date(value);
    } else {
      if (typeof value === 'string' && value.endsWith('Z')) return value;
      // assume string
      date = new Date(value);
    }
    // Build ISO string using the date components treated as UTC
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const milliseconds = String(date.getMilliseconds()).padStart(3, '0');

    // Return in ISO format with UTC timezone (Z = +00:00)
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`;
  }
}